// App-wide unread state for WhatsApp GROUP chats, feeding the "Groups" lens badge on the
// Messages page. Group threads live outside the default thread scope (the server excludes
// `whatsapp_group` everywhere by default), so the existing overview/needs counts never see
// them — this store owns their unread pile via the scope=groups summary.
//
// Clones chats-unread-store (the internal Chats sibling): a module-level cache fed by
// GET /threads/unread-summary?scope=groups that refreshes on login/permission changes, on WS
// group messages (debounced) and on a slow interval. Opened threads update optimistically via
// markWaGroupThreadReadLocally; the next summary fetch reconciles. No mute handling — chat
// mutes are an internal-Chats concept and never apply to group rows.

import { fetchThreadsUnreadSummary } from '@/lib/api/threads';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { isWaGroupMessage } from '@/lib/messages/wa-groups';
import { subscribeChatMessages } from '@/lib/realtime/chat-realtime-service';
import { hasPermission, subscribePermissions } from '@/lib/stores/permissions-store';

const NETWORK_REFRESH_MS = 60000;
// Message on a KNOWN group thread: refresh quickly — this is exactly the badge's job.
const WS_KNOWN_DEBOUNCE_MS = 800;
// Message on an UNKNOWN thread that still classifies as a group message (a brand-new group's
// first message): refresh too, but lazily — a busy group must not hammer the summary endpoint.
const WS_UNKNOWN_DEBOUNCE_MS = 2500;

let byThreadId = {}; // threadId -> unread count (>0 only)
let unreadThreadsCount = 0;
// Every group thread id this session has seen (summary responses + the Groups lens's loaded
// rows). Superset of the unread keys; picks the fast vs. lazy WS debounce above.
const waGroupThreadIds = new Set();
let snapshotOwnerUserId = '';
let started = false;
let wsDebounceTimer = null;
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener(unreadThreadsCount);
    } catch {
      /* ignore listener failures */
    }
  }
}

function setCounts(nextByThreadId) {
  byThreadId = nextByThreadId;
  unreadThreadsCount = Object.keys(byThreadId).length;
  // Emit unconditionally: even with an unchanged badge count the per-thread numbers may
  // have moved, and subscribers re-read the map.
  emit();
}

// On a profile switch the previous user's counts must drop immediately — the lens badge
// renders inside the new user's session before their first fetch resolves.
function dropIfOwnerChanged(currentUserId) {
  if (snapshotOwnerUserId === currentUserId) return;
  snapshotOwnerUserId = currentUserId;
  waGroupThreadIds.clear();
  if (Object.keys(byThreadId).length > 0 || unreadThreadsCount !== 0) {
    byThreadId = {};
    unreadThreadsCount = 0;
    emit();
  }
}

// Group chats render inside the Messages page, so the badge follows the Messages permission
// (the internal sibling keys on chats:view for the same reason).
function waGroupsEligible() {
  return Boolean(getAuthToken()) && hasPermission('messages', 'view');
}

export async function refreshWaGroupsUnread() {
  if (!waGroupsEligible()) {
    dropIfOwnerChanged(String(getAuthUser()?.id || '').trim());
    if (Object.keys(byThreadId).length > 0 || unreadThreadsCount !== 0) {
      byThreadId = {};
      unreadThreadsCount = 0;
      emit();
    }
    return;
  }
  const requestUserId = String(getAuthUser()?.id || '').trim();
  dropIfOwnerChanged(requestUserId);
  try {
    const { byThreadId: next } = await fetchThreadsUnreadSummary({ scope: 'groups' });
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) return;
    for (const tid of Object.keys(next)) {
      waGroupThreadIds.add(tid);
    }
    setCounts(next);
  } catch {
    /* keep the previous (same-user) counts; the next tick retries */
  }
}

function scheduleWsRefresh(delayMs) {
  if (wsDebounceTimer) return;
  wsDebounceTimer = setTimeout(() => {
    wsDebounceTimer = null;
    void refreshWaGroupsUnread();
  }, delayMs);
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshWaGroupsUnread();
  // Never cleared — the store lives for the app session, like the other module stores.
  setInterval(() => void refreshWaGroupsUnread(), NETWORK_REFRESH_MS);
  subscribeAuthStateChange(() => void refreshWaGroupsUnread());
  // Messages eligibility can arrive after login (permissions load/refresh) — kick then too.
  subscribePermissions(() => void refreshWaGroupsUnread());
  // Every chat message lands here; group ones move counts. The `@g.us` remoteJid classifies
  // even messages on threads this session hasn't listed yet. update:true is an in-place edit
  // (reaction, soft-delete notice) and never changes unread.
  subscribeChatMessages((detail) => {
    if (detail?.kind !== 'message' || detail?.update) return;
    if (!waGroupsEligible()) return;
    if (!isWaGroupMessage(detail?.message)) return;
    const tid = String(detail?.message?.threadId || '').trim();
    if (!tid) return;
    scheduleWsRefresh(waGroupThreadIds.has(tid) ? WS_KNOWN_DEBOUNCE_MS : WS_UNKNOWN_DEBOUNCE_MS);
  });
}

/** Number of WhatsApp group chats with unread messages (0 until the first fetch). */
export function getWaGroupsUnreadThreadsCount() {
  ensureStarted();
  return unreadThreadsCount;
}

/** threadId -> unread count map (read-only; >0 entries only). */
export function getWaGroupsUnreadByThreadId() {
  ensureStarted();
  return byThreadId;
}

export function subscribeWaGroupsUnread(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Whether the thread is a KNOWN WhatsApp group this session. False only means "not seen
 * yet" — callers with a threadId miss should fall back to the thread's own metadata.
 */
export function isKnownWaGroupThreadId(threadId) {
  ensureStarted();
  return waGroupThreadIds.has(String(threadId || '').trim());
}

/**
 * Feed group thread ids discovered elsewhere (the Groups lens's loaded window rows), so the
 * fast WS debounce applies even before the first summary response.
 */
export function noteWaGroupThreadIds(ids) {
  ensureStarted();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || '').trim();
    if (id) waGroupThreadIds.add(id);
  }
}

/**
 * Optimistically zero a thread's unread count (thread opened). Safe to call with any thread
 * id — non-group ids are never in the map, so the call is a no-op for them.
 */
export function markWaGroupThreadReadLocally(threadId) {
  const tid = String(threadId || '').trim();
  if (!tid || !(tid in byThreadId)) return;
  const next = { ...byThreadId };
  delete next[tid];
  setCounts(next);
}
